import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { Box, Typography, Container as MuiContainer, CircularProgress } from '@mui/material';
import { getPublicOverview } from 'services/transparency-service';
import { TransparencyHeader } from './components/TransparencyHeader';
import { FrameworkBadges } from './components/FrameworkBadges';
import { GovernanceOverview } from './components/GovernanceOverview';
import { TrendIndicator } from './components/TrendIndicator';
import { PublicPolicies } from './components/PublicPolicies';
import { TransparencyPageContainer, TransparencyPageContent, SectionTitle } from './styles';

/**
 * PublicTransparencyPage
 * - No authentication required
 * - Accessed via /transparency/:companyKey (no token param)
 * - Shows high-level transparency information only
 * - noindex/nofollow enforced
 */
export const PublicTransparencyPage = ({ companyKey }) => {
  const { isLoading, error, data } = useQuery(
    ['publicTransparency', companyKey],
    () => getPublicOverview(companyKey),
    { enabled: !!companyKey }
  );

  const organizationData = data?.data;

  // SEO: Prevent indexing (Story-008 requirement)
  // Must be called before any early returns to follow React hooks rules
  useEffect(() => {
    document.title = organizationData?.organization?.name || 'Centro de Transparência';
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, [organizationData?.organization?.name]);

  if (isLoading) {
    return (
      <TransparencyPageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </TransparencyPageContainer>
    );
  }

  if (error || !organizationData) {
    return (
      <TransparencyPageContainer>
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h5" color="text.secondary">
            Esta página de transparência não está disponível.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            A empresa pode não ter habilitado o centro de transparência público.
          </Typography>
        </Box>
      </TransparencyPageContainer>
    );
  }

  const { organization, frameworkBadges, trendIndicator, governanceStructure, policies } = organizationData;

  return (
    <TransparencyPageContainer>
      <TransparencyHeader
        name={organization?.name}
        description={organization?.description}
        lastUpdated={organization?.lastUpdated}
      />

      <TransparencyPageContent>
        <MuiContainer maxWidth="lg">
          {trendIndicator && (
            <Box sx={{ mb: 4 }}>
              <TrendIndicator
                label={trendIndicator.label}
                description={trendIndicator.description}
                trend={trendIndicator.trend}
              />
            </Box>
          )}

          {frameworkBadges && frameworkBadges.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>Nossos Compromissos</SectionTitle>
              <FrameworkBadges badges={frameworkBadges} />
            </Box>
          )}

          {governanceStructure && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>Estrutura de Governança</SectionTitle>
              <GovernanceOverview
                categories={governanceStructure.categories}
                overallPosture={governanceStructure.overallPosture}
              />
            </Box>
          )}

          {policies && policies.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>Políticas Públicas</SectionTitle>
              <PublicPolicies policies={policies} />
            </Box>
          )}
        </MuiContainer>
      </TransparencyPageContent>
    </TransparencyPageContainer>
  );
};
